import { datadogRum } from '@datadog/browser-rum'

let isInitialized = false

export const HTTP_UNAUTHORIZED_CODE = 401
export const HTTP_FORBIDDEN_CODE = 403
export const HTTP_NOT_FOUND_CODE = 404
export const HTTP_UNPROCESSABLE_ENTITY_CODE = 422
export const HTTP_TOO_MANY_REQUESTS_CODE = 429

export const RUM_SKIP_CLIENT_ERROR_STATUS_CODES = [
  HTTP_UNAUTHORIZED_CODE,
  HTTP_FORBIDDEN_CODE,
  HTTP_NOT_FOUND_CODE,
  HTTP_UNPROCESSABLE_ENTITY_CODE,
  HTTP_TOO_MANY_REQUESTS_CODE,
]

const RUM_IGNORED_FETCHES = [
  '_boost/browser-logs',
  'addtoany.com',
  'canny.io',
  'customer.io',
  'facebook.com',
  'facebook.net',
  'freshdesk.com',
  'fullstory.com',
  'google-analytics.com',
  'google.com',
  'googleads',
  'googleapis.com',
  'googletagmanager.com',
  'intercom.io',
  'jsdelivr.net',
  'mapbox.com',
  'node_modules',
  'small.chat',
  'ucarecdn.com',
  'usefathom.com',
  'vitally.io',
  'zendesk.com',
]

const ERROR_MESSAGES_TO_DISCARD = [
  // https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
  'Object Not Found Matching Id:',
  'Script error',
  'Network Error',
  'Request aborted',
  'prop must be one of the following',
  'Failed to fetch',
  'The operation is insecure',
]

// https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/
function isBotUserAgent() {
  const botPattern =
    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis|bot|crawler|spider|headlesschrome)'

  const regex = new RegExp(botPattern, 'i')

  return navigator?.userAgent && regex.test(navigator.userAgent)
}

export function shouldSkipRumClientErrorStatus(status) {
  const statusCode = parseInt(status, 10)

  return RUM_SKIP_CLIENT_ERROR_STATUS_CODES.includes(statusCode)
}

/**
 * Whether to discard a resource event.
 *
 * @param {Object} event - The resource event
 * @param {Object} _context - The context
 *
 * @returns {boolean}
 */
function shouldDiscard(event, _context) {
  // Alternatively, discard if the event is a known bot from Datadog's built-in parsing
  if (event.device && event.device.type === 'bot') {
    return true
  }

  if (
    event.type === 'resource' &&
    ['fetch', 'xhr', 'image', 'media', 'font', 'js', 'css', 'other'].includes(event.resource.type)
  ) {
    if (shouldSkipRumClientErrorStatus(event.resource.status_code)) {
      return true
    }

    return RUM_IGNORED_FETCHES.some(
      (host) => typeof event.resource.url === 'string' && event.resource.url.includes(host)
    )
  }

  if (event.type === 'error') {
    if (shouldSkipRumClientErrorStatus(event.context?.status)) {
      return true
    }

    if (event.error?.message) {
      return ERROR_MESSAGES_TO_DISCARD.some((message) => event.error.message.includes(message))
    }
  }

  return false
}

export function getAllowedTracingUrls() {
  const urls = []

  if (window.location?.origin) {
    urls.push(window.location.origin)
  }

  // Sentry uses the same baseUrl pattern for API propagation
  const apiBaseUrl = window.ARYEO_COMPANY_API_V1_BASE_URL

  if (apiBaseUrl && !urls.some((url) => apiBaseUrl.startsWith(url))) {
    urls.push(apiBaseUrl)
  }

  return urls
}

const isValidSampleRate = (sampleRate) =>
  !Number.isNaN(sampleRate) && sampleRate > 0 && sampleRate <= 100

const isLoginPage = () =>
  window.location?.pathname?.includes('/login') || window.location?.pathname?.includes('/portal')

/**
 * Initialize Datadog RUM when config is present.
 * Call once from app entry (app.js).
 */
export function initDatadogRum() {
  const applicationId = window.ARYEO_DATADOG_RUM_APPLICATION_ID
  const clientToken = window.ARYEO_DATADOG_RUM_CLIENT_TOKEN
  const sessionSampleRate = parseInt(window.ARYEO_DATADOG_RUM_SESSION_SAMPLE_RATE, 10)

  if (
    !applicationId ||
    !clientToken ||
    !isValidSampleRate(sessionSampleRate) ||
    isBotUserAgent() ||
    isLoginPage() || // Avoiding creating an extra billable anonymous session on login page
    isInitialized
  ) {
    return
  }

  const initConfig = {
    applicationId,
    clientToken,
    site: window.ARYEO_DATADOG_RUM_SITE,
    service: window.ARYEO_DATADOG_RUM_SERVICE,
    env: window.ARYEO_DATADOG_RUM_ENV,
    version: window.ARYEO_DATADOG_RUM_VERSION,
    sessionSampleRate: sessionSampleRate,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true, // We track views manually in app.js through startRumView() on router 'navigate' event.
    allowedTracingUrls: getAllowedTracingUrls(),
    traceContextInjection: 'sampled',
    beforeSend: (event, context) => !shouldDiscard(event, context),
  }

  const traceSampleRate = parseInt(window.ARYEO_DATADOG_RUM_TRACE_SAMPLE_RATE, 10)

  if (isValidSampleRate(traceSampleRate)) {
    initConfig.traceSampleRate = traceSampleRate
  }

  datadogRum.init(initConfig)

  if (window.ARYEO_DATADOG_RUM_USER) {
    datadogRum.setUser(window.ARYEO_DATADOG_RUM_USER)
  }

  isInitialized = true
}

/**
 * Get the view name from an Inertia page object.
 * If the page object has a component property, return it.
 * Otherwise, return the window.location.pathname.
 *
 * @param {Object} page - Inertia Page Object
 *
 * @returns string
 */
export function getViewNameFromPage(page) {
  return page?.component ?? window.location.pathname
}

/**
 * Get the context from a view name.
 * The view name is in the format: UserType/Domain/Process/Component
 *
 * @param {String} viewName - The view name (e.g. Customer/OrderForms/OrderForm/ShowOrderFormAppointmentStep)
 *
 * @returns {Object}
 * @property {String} user_type - The type of user (Customer or Company)
 * @property {String} domain - The domain of the view (OrderForms)
 * @property {String} process - The process of the view (OrderForm)
 */
function getContextFromViewName(viewName) {
  if (typeof viewName !== 'string' || !viewName.includes('/')) {
    return {}
  }

  const [userType, domain, process] = viewName.split('/')

  return {
    user_type: userType,
    domain,
    process,
  }
}

/**
 * Remove undefined, null, and empty values from a context object.
 *
 * @param {Record<string, unknown>} context - The context object
 *
 * @returns {Record<string, unknown>}
 */
export function cleanContext(context) {
  return Object.fromEntries(
    Object.entries(context).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
}

/**
 * Start a RUM view (for Inertia navigation).
 * Call on router 'navigate' with the resolved page component name.
 *
 * @param {string} viewName - View name (e.g. Inertia component path or route name)
 * @param {Record<string, unknown>} [context] - Optional view context
 */
export function startRumView(viewName, context = {}) {
  if (!isInitialized) {
    return
  }

  try {
    const viewContext = getContextFromViewName(viewName)

    datadogRum.startView({
      name: viewName,
      context: cleanContext({ ...viewContext, ...context }),
    })
  } catch (error) {
    console.error('Error starting RUM view', error)
  }
}

/**
 * Add a custom RUM action (for funnels and business events).
 *
 * @param {string} name - Action name (e.g. 'order_form.step.address')
 * @param {Record<string, string|number|boolean|undefined>} [context] - Action context (no PII)
 */
export function addRumAction(name, context = {}) {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.addAction(name, cleanContext(context))
  } catch (error) {
    console.error('Error adding RUM action', error)
  }
}

export function addRumError(error, context = {}) {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.addError(error, context)
  } catch (error) {
    console.error('Error adding RUM error', error)
  }
}

/**
 * Whether RUM is currently initialized (for conditional tracking in components).
 */
export function isDatadogRumInitialized() {
  return isInitialized
}
